import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';

// Mock data for automated investing
const mockPortfolio = {
  totalValue: 0,
  monthlyContribution: 500,
  riskLevel: 'moderate', // conservative, moderate, aggressive
  accounts: [
    { id: 1, name: 'Taxable Brokerage', type: 'brokerage', provider: 'Thrynx Invest' }
  ],
  strategy: {
    name: 'Boglehead Three-Fund',
    description: 'A simple, diversified portfolio based on the Bogleheads investment philosophy',
    allocations: [
      { symbol: 'VTI', name: 'Vanguard Total Stock Market', target: 60, color: 'bg-blue-500' },
      { symbol: 'VXUS', name: 'Vanguard Total International', target: 30, color: 'bg-green-500' },
      { symbol: 'BND', name: 'Vanguard Total Bond Market', target: 10, color: 'bg-yellow-500' }
    ]
  },
  nextInvestmentDate: '2025-07-01',
  status: 'inactive' // inactive, active, paused
};

const Investments = () => {
  const { user } = useUser();
  const [portfolio, setPortfolio] = useState(null);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [formData, setFormData] = useState({
    initialDeposit: 1000,
    monthlyContribution: 500,
    riskLevel: 'moderate',
    strategy: 'boglehead',
    fundingAccount: ''
  });

  useEffect(() => {
    // Simulate loading user's portfolio
    const timer = setTimeout(() => {
      setPortfolio(mockPortfolio);
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'monthlyContribution' || name === 'initialDeposit' 
        ? Math.max(0, parseInt(value, 10) || 0)
        : value
    }));
  };

  const handleStartInvesting = (e) => {
    e.preventDefault();
    // In a real app, this would connect to your backend
    setPortfolio({
      ...mockPortfolio,
      totalValue: formData.initialDeposit,
      monthlyContribution: formData.monthlyContribution,
      riskLevel: formData.riskLevel,
      status: 'active',
      nextInvestmentDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 30 days from now
    });
    setIsSettingUp(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (!portfolio) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Automated Investing</h1>
        <p className="text-lg text-gray-600">
          Let your money work for you with our smart, automated index fund portfolio
        </p>
      </div>

      {portfolio.status === 'inactive' && !isSettingUp ? (
        <div className="bg-white rounded-xl shadow-sm p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-24 w-24 rounded-full bg-blue-100 mb-6">
            <svg className="h-12 w-12 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Start Investing Automatically</h2>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Set up automated investments in a diversified portfolio of low-cost index funds.
            We'll handle the rest, including automatic rebalancing.
          </p>
          <button
            onClick={() => setIsSettingUp(true)}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Get Started
          </button>
        </div>
      ) : isSettingUp ? (
        <div className="bg-white rounded-xl shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Set Up Automated Investing</h2>
          
          <form onSubmit={handleStartInvesting} className="space-y-6">
            <div>
              <label htmlFor="initialDeposit" className="block text-sm font-medium text-gray-700 mb-1">
                Initial Investment
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  name="initialDeposit"
                  id="initialDeposit"
                  min="0"
                  value={formData.initialDeposit}
                  onChange={handleInputChange}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label htmlFor="monthlyContribution" className="block text-sm font-medium text-gray-700 mb-1">
                Monthly Contribution
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">$</span>
                </div>
                <input
                  type="number"
                  name="monthlyContribution"
                  id="monthlyContribution"
                  min="0"
                  value={formData.monthlyContribution}
                  onChange={handleInputChange}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-7 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="0"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Risk Level
              </label>
              <div className="grid grid-cols-3 gap-3 mt-1">
                {[
                  { value: 'conservative', label: 'Conservative', bonds: '40%' },
                  { value: 'moderate', label: 'Moderate', bonds: '20%' },
                  { value: 'aggressive', label: 'Aggressive', bonds: '10%' }
                ].map((option) => (
                  <div key={option.value} className="relative">
                    <input
                      type="radio"
                      id={`risk-${option.value}`}
                      name="riskLevel"
                      value={option.value}
                      checked={formData.riskLevel === option.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <label
                      htmlFor={`risk-${option.value}`}
                      className={`block cursor-pointer rounded-lg border px-4 py-3 text-center text-sm font-medium ${
                        formData.riskLevel === option.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <span className="block font-medium">{option.label}</span>
                      <span className="text-xs text-gray-500">{option.bonds} bonds</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Portfolio Strategy</h3>
              <div className="space-y-3">
                <div className="relative">
                  <input
                    type="radio"
                    id="strategy-boglehead"
                    name="strategy"
                    value="boglehead"
                    checked={formData.strategy === 'boglehead'}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <label
                    htmlFor="strategy-boglehead"
                    className={`block cursor-pointer rounded-lg border p-4 ${
                      formData.strategy === 'boglehead' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                          formData.strategy === 'boglehead' 
                            ? 'border-blue-500 bg-blue-500' 
                            : 'border-gray-300 bg-white'
                        }`}>
                          {formData.strategy === 'boglehead' && (
                            <svg className="h-3.5 w-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-gray-900">Boglehead Three-Fund</h4>
                        <p className="mt-1 text-sm text-gray-500">
                          A simple, diversified portfolio of total market index funds (US, International, Bonds)
                        </p>
                      </div>
                    </div>
                  </label>
                </div>

                <div className="relative">
                  <input
                    type="radio"
                    id="strategy-target"
                    name="strategy"
                    value="target"
                    checked={formData.strategy === 'target'}
                    onChange={handleInputChange}
                    className="sr-only"
                    disabled
                  />
                  <label
                    htmlFor="strategy-target"
                    className={`block rounded-lg border p-4 ${
                      formData.strategy === 'target' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="h-5 w-5 rounded-full border border-gray-300 bg-white"></div>
                      </div>
                      <div className="ml-3">
                        <div className="flex items-center">
                          <h4 className="text-sm font-medium text-gray-700">Target Date Fund</h4>
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Coming Soon
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          Automatically adjusts your asset allocation as you approach your target date
                        </p>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setIsSettingUp(false)}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Start Investing
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Portfolio Summary */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{formatCurrency(portfolio.totalValue)}</h2>
                <p className="text-sm text-gray-500">Total Portfolio Value</p>
              </div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                Active
              </span>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Portfolio Allocation</h3>
              <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                {portfolio.strategy.allocations.map((asset, index) => (
                  <div
                    key={asset.symbol}
                    className={`h-full ${asset.color}`}
                    style={{ width: `${asset.target}%`, display: 'inline-block' }}
                    title={`${asset.name}: ${asset.target}%`}
                  ></div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap gap-4">
                {portfolio.strategy.allocations.map((asset) => (
                  <div key={asset.symbol} className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${asset.color} mr-2`}></div>
                    <span className="text-xs text-gray-600">
                      {asset.symbol} - {asset.target}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Next Investment */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Next Investment</h3>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Your next automatic investment of {formatCurrency(portfolio.monthlyContribution)} is scheduled for {portfolio.nextInvestmentDate}
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex space-x-3">
              <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                Skip Next
              </button>
              <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                Edit Schedule
              </button>
            </div>
          </div>

          {/* Strategy Details */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Your Investment Strategy</h3>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h4 className="text-base font-medium text-gray-900">{portfolio.strategy.name}</h4>
                <p className="mt-1 text-sm text-gray-500">{portfolio.strategy.description}</p>
              </div>
              <div className="bg-gray-50 px-4 py-3 text-sm text-gray-500">
                <div className="grid grid-cols-3 gap-4">
                  <div>Asset</div>
                  <div className="text-right">Target</div>
                  <div className="text-right">Current</div>
                </div>
              </div>
              <ul className="divide-y divide-gray-200">
                {portfolio.strategy.allocations.map((asset) => (
                  <li key={asset.symbol} className="px-4 py-3">
                    <div className="grid grid-cols-3 gap-4 items-center">
                      <div className="flex items-center">
                        <div className={`w-2.5 h-2.5 rounded-full ${asset.color} mr-2`}></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{asset.symbol}</div>
                          <div className="text-xs text-gray-500">{asset.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-900">{asset.target}%</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-900">
                          {Math.round(portfolio.totalValue * (asset.target / 100)).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4 flex justify-end">
              <button className="text-sm font-medium text-blue-600 hover:text-blue-800">
                View Full Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Investments;
